import { useMemo } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { news } from "@/mocks/news";
import { newsCategoryMeta } from "@/mocks/categories";
import type { NewsCategory } from "@/types";
import { useSEO } from "@/hooks/useSEO";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";

const PAGE_SIZE = 3;
const valid: NewsCategory[] = ["industry", "company", "release", "interview"];

export default function NewsListPage() {
  const { category } = useParams<{ category: string }>();
  const key = (valid.includes(category as NewsCategory) ? category : "industry") as NewsCategory;
  const meta = newsCategoryMeta[key];

  const [searchParams, setSearchParams] = useSearchParams();
  const page = Number(searchParams.get("page") || "1");

  useSEO({
    title: `${meta.label} - 星光传媒影视平台`,
    description: `${meta.intro} 及时获取星光传媒与影视行业的最新资讯。`,
    keywords: `星光传媒,${meta.label},影视资讯,行业动态`,
  });

  const list = useMemo(
    () =>
      news
        .filter((item) => item.category === key)
        .sort((a, b) => (a.date < b.date ? 1 : -1)),
    [key]
  );

  const totalPages = Math.max(1, Math.ceil(list.length / PAGE_SIZE));
  const currentPage = Math.min(Math.max(1, page), totalPages);
  const paged = list.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const goPage = (next: number) => {
    setSearchParams({ page: String(next) });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6 md:py-12">
      <Breadcrumb
        items={[{ label: "首页", to: "/" }, { label: "影视资讯" }, { label: meta.label }]}
      />

      <header className="mt-4 border-b border-background-200 pb-6">
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
          {meta.label}
        </h1>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600">{meta.intro}</p>
      </header>

      <div className="mt-6 flex flex-col gap-8 lg:flex-row">
        <div className="min-w-0 flex-1">
          {paged.length === 0 ? (
            <EmptyState
              icon="ri-newspaper-line"
              title="暂无相关资讯"
              desc="该栏目暂时没有内容，欢迎浏览其他资讯栏目。"
              action={
                <Link
                  to="/news/industry"
                  className="whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
                >
                  查看行业动态
                </Link>
              }
            />
          ) : (
            <ul className="flex flex-col gap-4">
              {paged.map((item) => (
                <li key={item.id}>
                  <Link
                    to={`/detail/news/${item.id}`}
                    className="group flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-accent-300 sm:flex-row"
                  >
                    <div className="h-40 w-full shrink-0 overflow-hidden rounded-md bg-secondary-100 sm:h-28 sm:w-44">
                      <img
                        src={item.cover}
                        alt={item.title}
                        title={`${item.title} - 星光传媒资讯`}
                        className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-3 text-xs text-foreground-500">
                        <span className="rounded bg-secondary-100 px-1.5 py-0.5 text-secondary-700">
                          {meta.label}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-calendar-line" />
                          {item.date}
                        </span>
                        <span className="hidden items-center gap-1 sm:flex">
                          <i className="ri-newspaper-line" />
                          {item.source}
                        </span>
                      </div>
                      <h2 className="mt-2 text-base font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-accent-600 md:text-lg">
                        {item.title}
                      </h2>
                      <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">
                        {item.summary}
                      </p>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}

          {totalPages > 1 && (
            <div className="mt-8 flex items-center justify-center gap-2">
              <button
                type="button"
                disabled={currentPage === 1}
                onClick={() => goPage(currentPage - 1)}
                className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 text-foreground-600 transition-colors enabled:hover:border-accent-400 enabled:hover:text-accent-600 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <i className="ri-arrow-left-s-line text-xl" />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((num) => (
                <button
                  key={num}
                  type="button"
                  onClick={() => goPage(num)}
                  className={`flex h-9 min-w-9 items-center justify-center rounded-md px-3 text-sm font-medium transition-colors ${
                    num === currentPage
                      ? "bg-primary-500 text-background-50"
                      : "border border-background-300 text-foreground-700 hover:border-accent-400 hover:text-accent-600"
                  }`}
                >
                  {num}
                </button>
              ))}
              <button
                type="button"
                disabled={currentPage === totalPages}
                onClick={() => goPage(currentPage + 1)}
                className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 text-foreground-600 transition-colors enabled:hover:border-accent-400 enabled:hover:text-accent-600 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <i className="ri-arrow-right-s-line text-xl" />
              </button>
            </div>
          )}
        </div>

        {/* Sidebar: other categories */}
        <aside className="w-full shrink-0 lg:w-64">
          <div className="sticky top-24 rounded-lg border border-background-200 bg-background-50 p-5">
            <h3 className="mb-4 font-heading text-base font-semibold text-foreground-950">
              资讯栏目
            </h3>
            <div className="flex flex-col gap-1">
              {valid.map((item) => (
                <Link
                  key={item}
                  to={`/news/${item}`}
                  className={`flex items-center justify-between rounded-md px-3 py-2.5 text-sm transition-colors ${
                    item === key
                      ? "bg-accent-500 text-background-50"
                      : "text-foreground-700 hover:bg-background-100"
                  }`}
                >
                  {newsCategoryMeta[item].label}
                  <i className="ri-arrow-right-s-line opacity-70" />
                </Link>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}